import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { LoadingSpinner } from "../LoadingSpinner";
import { ErrorState } from "../ErrorState";
import { EmptyState } from "../EmptyState";
import { ConfirmDialog } from "../ConfirmDialog";
import { listStaff, addStaff, removeStaff } from "../../services/staff.service";
import { getApiErrorMessage } from "../../lib/apiClient";

export function StaffManagementPanel({ eventId }: { eventId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId, "staff"],
    queryFn: () => listStaff(eventId),
  });

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; name: string } | null>(null);

  const addMutation = useMutation({
    mutationFn: () => addStaff(eventId, email, name || undefined, password || undefined),
    onSuccess: () => {
      toast.success("Staff added");
      setEmail("");
      setName("");
      setPassword("");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "staff"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeStaff(eventId, userId),
    onSuccess: () => {
      toast.success("Staff removed");
      setRemoveTarget(null);
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "staff"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    addMutation.mutate();
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="mb-6 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="label" htmlFor="staff-email">
              Staff email
            </label>
            <input
              id="staff-email"
              type="email"
              className="input"
              placeholder="staff@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="label" htmlFor="staff-name">
              Name (optional)
            </label>
            <input id="staff-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex-1">
            <label className="label" htmlFor="staff-password">
              Password (optional)
            </label>
            <input
              id="staff-password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              placeholder="Leave blank for Google sign-in only"
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn-primary" disabled={addMutation.isPending}>
            {addMutation.isPending ? "Adding..." : "Add Staff"}
          </button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          To sign in with Google, the staff member's Google account email must match this email exactly. Setting a
          password (min 8 characters) also lets them log in with email + password — safe to re-run this form on an
          existing staff member just to set/reset their password.
        </p>
      </form>

      {isLoading && <LoadingSpinner />}
      {isError && <ErrorState message={getApiErrorMessage(error)} />}
      {data && data.length === 0 && <EmptyState title="No staff assigned" description="Add staff by their Google account email." />}

      {data && data.length > 0 && (
        <div className="card divide-y divide-slate-100 dark:divide-slate-700 animate-fade-in">
          {data.map((assignment) => (
            <div key={assignment.id} className="flex items-center justify-between p-4">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">{assignment.user.name}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{assignment.user.email}</p>
              </div>
              <button
                type="button"
                className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-150"
                onClick={() => setRemoveTarget({ userId: assignment.userId, name: assignment.user.name })}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove staff"
        message={`Remove ${removeTarget?.name} from this event's staff?`}
        confirmLabel="Remove"
        danger
        isLoading={removeMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.userId)}
      />
    </div>
  );
}
